// Imports
import PropTypes from 'prop-types'
import { createSelector } from 'reselect'
import uniqBy from 'lodash/uniqBy'
import API from '../../../utils/http/api'
import selectors from '../../../store/shared/selectors'
import { graphAPI } from '../../../utils/graphql/graphql'
import notificationsUtils from '../../../utils/notifications/notificationsUtils'

// Actions
const JOB_SEARCH_REQUEST = 'steppingblocks/jobSearch/REQUEST'
const JOB_SEARCH_SUCCESS = 'steppingblocks/jobSearch/SUCCESS'
const JOB_SEARCH_FAILURE = 'steppingblocks/jobSearch/FAILURE'
const JOB_SEARCH_LOAD_MORE_SUCCESS = 'steppingblocks/jobSearch/LOAD_MORE_SUCCESS'
const RESET = 'steppingblocks/jobSearch/RESET'
const OPEN_JOB_DETAIL_MODAL = 'steppingblocks/jobSearch/OPEN_JOB_DETAIL_MODAL'
const CLOSE_JOB_DETAIL_MODAL = 'steppingblocks/jobSearch/CLOSE_JOB_DETAIL_MODAL'
const FETCH_JOB_POSTING_DETAILS = 'steppingblocks/jobSearch/FETCH_JOB_POSTING_DETAILS'
const FETCH_JOB_POSTING_DETAILS_SUCCESS =
  'steppingblocks/jobSearch/FETCH_JOB_POSTING_DETAILS_SUCCESS'
const FETCH_JOB_POSTING_DETAILS_FAILURE =
  'steppingblocks/jobSearch/FETCH_JOB_POSTING_DETAILS_FAILURE'

// Reducer
const initialState = {
  error: null,
  requestStarted: false,
  requestEnded: false,
  jobs: [],
  jobDetailsModalShown: false,
  pagination: {
    total: 0,
    currentPage: 0,
    totalPages: 0,
    firstItemIndex: 0,
    lastItemIndex: 0,
  },
  payFilter: null,
  selectedJobPosting: null,
  loadingJobPostingDetails: false,
  jobPostingDetails: null,
}

export default (_state = initialState, action) => {
  const { type, payload, payFilter } = action
  switch (type) {
    case JOB_SEARCH_REQUEST:
      return {
        ..._state,
        error: null,
        requestStarted: true,
        requestEnded: false,
      }
    case JOB_SEARCH_SUCCESS:
      return {
        ..._state,
        requestEnded: true,
        jobs: payload.jobs,
        pagination: payload.pagination,
        payFilter,
      }
    case JOB_SEARCH_LOAD_MORE_SUCCESS:
      return {
        ..._state,
        requestEnded: true,
        jobs: _state.jobs.concat(payload.jobs),
        pagination: payload.pagination,
      }
    case JOB_SEARCH_FAILURE:
      return {
        ..._state,
        requestEnded: true,
        error: payload.error,
      }
    case OPEN_JOB_DETAIL_MODAL:
      return {
        ..._state,
        jobDetailsModalShown: true,
      }
    case CLOSE_JOB_DETAIL_MODAL:
      return {
        ..._state,
        jobDetailsModalShown: false,
      }
    case FETCH_JOB_POSTING_DETAILS:
      return {
        ..._state,
        loadingJobPostingDetails: true,
        jobDetailsModalShown: true,
        selectedJobPosting: payload.data,
      }
    case FETCH_JOB_POSTING_DETAILS_SUCCESS:
      return {
        ..._state,
        loadingJobPostingDetails: false,
        jobPostingDetails: payload.data,
      }
    case RESET:
      return initialState
    default:
      return _state
  }
}

// Action Performers
export const resetJobSearch = () => ({
  type: RESET,
})

export const performJobsSearch = params => async dispatch => {
  dispatch({ type: JOB_SEARCH_REQUEST })
  try {
    const data = await API.get('/jobs/search', params)
    dispatch({
      type: params.page && params.page > 1 ? JOB_SEARCH_LOAD_MORE_SUCCESS : JOB_SEARCH_SUCCESS,
      payload: {
        jobs: data.results || [],
        pagination: {
          total: data.totalCount,
          currentPage: data.currentPage,
          totalPages: data.totalPages,
          firstItemIndex: data.firstItemIndex,
          lastItemIndex: data.lastItemIndex,
        },
      },
      payFilter: params.pay,
    })
    return data
  } catch (error) {
    dispatch({
      type: JOB_SEARCH_FAILURE,
      payload: { error: error.message },
    })
    return error
  }
}

export const performFederalJobsSearch = params => async dispatch => {
  dispatch({ type: JOB_SEARCH_REQUEST })
  try {
    const data = await API.get('/jobs/search/federal', params)
    dispatch({
      type: params.page && params.page > 1 ? JOB_SEARCH_LOAD_MORE_SUCCESS : JOB_SEARCH_SUCCESS,
      payload: {
        jobs: data.results || [],
        pagination: {
          total: data.totalCount,
          currentPage: params.page,
          totalPages: data.totalPages,
        },
      },
      payFilter: params.pay,
    })
    return data
  } catch (error) {
    dispatch({
      type: JOB_SEARCH_FAILURE,
      payload: { error: error.message },
    })
    return error
  }
}

export const toggleJobDetailsModal = () => (dispatch, getState) => {
  const { jobSearch } = getState()
  if (jobSearch.jobDetailsModalShown) {
    dispatch({ type: CLOSE_JOB_DETAIL_MODAL })
  } else {
    dispatch({ type: OPEN_JOB_DETAIL_MODAL })
  }
}

const query = `
    query($jobId: String!) {
      job(jobId: $jobId)
  }
`

export const selectJobTitle = jobPosting => async dispatch => {
  try {
    dispatch({ type: FETCH_JOB_POSTING_DETAILS, payload: { data: jobPosting } })
    const response = await graphAPI(query, { jobId: jobPosting.job_id })

    dispatch({ type: FETCH_JOB_POSTING_DETAILS_SUCCESS, payload: { data: response.job } })
  } catch (error) {
    notificationsUtils.showErrorNotification('Something went wrong. Please try again.')
    dispatch({
      type: FETCH_JOB_POSTING_DETAILS_FAILURE,
      payload: { error: error.message },
    })
  }
}

// PropTypes
export const propTypes = PropTypes.shape({})

//
// Fixtures
export const testData = {}

// Selectors
const getJobsSearch = state => state.jobSearch

const getIsRequestSuccessful = state => selectors.getIsRequestSuccessful(state, 'jobSearch')

export const getJobs = createSelector(getJobsSearch, jobSearch =>
  uniqBy(jobSearch.jobs, 'JobDetailsURL'),
)

export const getIsEmptyData = createSelector(
  getIsRequestSuccessful,
  getJobsSearch,
  (requestSuccess, data) => requestSuccess && data && data.jobs.length === 0,
)



// WEBPACK FOOTER //
// ./src/routes/authenticated/JobSearch/jobSearchDuck.js